import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Loader2,
  MapPin,
  Calendar,
  DollarSign,
  FolderKanban,
  ChevronRight,
} from 'lucide-react';
import type { ApiResponse, Project, ProjectInput, ProjectStatus } from '@/types';

const STATUS_COLORS: Record<ProjectStatus, string> = {
  planning: 'bg-blue-500',
  active: 'bg-emerald-500',
  on_hold: 'bg-amber-500',
  completed: 'bg-slate-500',
};

export function ProjectsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [showNewProject, setShowNewProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // New project form
  const [name, setName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [budgetDollars, setBudgetDollars] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('planning');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: async (): Promise<Project[]> => {
      const res: ApiResponse<Project[]> = await api.get('/projects');
      return res.success ? (res.data ?? []) : [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (input: ProjectInput) => {
      await api.post('/projects', input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project created');
      setShowNewProject(false);
      resetForm();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to create project');
    },
  });

  const resetForm = () => {
    setName('');
    setSiteAddress('');
    setBudgetDollars('');
    setStatus('planning');
    setStartDate('');
    setEndDate('');
  };

  const handleCreateProject = () => {
    if (!name.trim()) {
      toast.error('Project name is required');
      return;
    }
    createMutation.mutate({
      name: name.trim(),
      siteAddress: siteAddress.trim() || undefined,
      status,
      budgetCents: budgetDollars ? Math.round(parseFloat(budgetDollars) * 100) : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground">Manage your construction projects</p>
        </div>
        <Button onClick={() => setShowNewProject(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Project
        </Button>
      </div>

      {/* Project Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="mb-2 h-5 w-3/4" />
                <Skeleton className="mb-4 h-4 w-1/2" />
                <Skeleton className="h-3 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-destructive">Failed to load projects</p>
            <Button
              variant="outline"
              className="mt-2"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['projects'] })}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (projects?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FolderKanban className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium">No projects yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Create your first construction project to get started
            </p>
            <Button className="mt-4" onClick={() => setShowNewProject(true)}>
              <Plus className="mr-2 h-4 w-4" /> New Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects?.map((project) => (
            <Card
              key={project.id}
              className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
              onClick={() => { setSelectedProject(project); setActiveTab('overview'); }}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold truncate">{project.name}</h3>
                    {project.siteAddress && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{project.siteAddress}</span>
                      </p>
                    )}
                  </div>
                  <div className={cn('ml-3 h-2.5 w-2.5 rounded-full shrink-0', STATUS_COLORS[project.status])} />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <Badge variant="outline">{project.status}</Badge>
                  {project.budgetCents != null && (
                    <span className="text-sm font-medium">{formatCurrency(project.budgetCents)}</span>
                  )}
                </div>
                <Progress value={project.status === 'completed' ? 100 : project.status === 'active' ? 50 : project.status === 'on_hold' ? 30 : 10} className="mt-3" />
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  {project.startDate ? (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(project.startDate)}
                    </span>
                  ) : (
                    <span>No start date</span>
                  )}
                  <ChevronRight className="h-4 w-4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Project Dialog */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogHeader>
          <DialogTitle>New Project</DialogTitle>
          <DialogDescription>Create a new construction project</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Project Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Smith Residence Renovation"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="siteAddress">Site Address</Label>
            <Input
              id="siteAddress"
              value={siteAddress}
              onChange={(e) => setSiteAddress(e.target.value)}
              placeholder="123 Example St, Sydney NSW 2000"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as ProjectStatus)}>
                <SelectItem value="planning">Planning</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget">Budget ($)</Label>
              <Input
                id="budget"
                type="number"
                value={budgetDollars}
                onChange={(e) => setBudgetDollars(e.target.value)}
                placeholder="500000"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowNewProject(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateProject} disabled={isCreating}>
            {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create Project
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Project Detail Dialog */}
      <Dialog open={selectedProject !== null} onOpenChange={() => setSelectedProject(null)}>
        {selectedProject && (
          <div className="max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{selectedProject.name}</DialogTitle>
              <DialogDescription>
                {selectedProject.siteAddress ?? 'No address'} ·{' '}
                <Badge variant="outline">{selectedProject.status}</Badge>
              </DialogDescription>
            </DialogHeader>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="schedule">Schedule</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="finances">Finances</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge>{selectedProject.status}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Budget</p>
                    <p className="font-medium">
                      {selectedProject.budgetCents != null
                        ? formatCurrency(selectedProject.budgetCents)
                        : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Start Date</p>
                    <p>{selectedProject.startDate ? formatDate(selectedProject.startDate) : 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">End Date</p>
                    <p>{selectedProject.endDate ? formatDate(selectedProject.endDate) : 'Not set'}</p>
                  </div>
                </div>
                <Card>
                  <CardHeader><CardTitle className="text-sm">Progress</CardTitle></CardHeader>
                  <CardContent>
                    <Progress
                      value={
                        selectedProject.status === 'completed' ? 100 :
                        selectedProject.status === 'active' ? 50 :
                        selectedProject.status === 'on_hold' ? 30 : 10
                      }
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="schedule">
                <div className="flex flex-col items-center py-8 text-center text-muted-foreground">
                  <Calendar className="mb-2 h-12 w-12 opacity-50" />
                  <p>Schedule features coming soon</p>
                  <p className="text-sm">Gantt charts and milestone tracking will appear here</p>
                </div>
              </TabsContent>

              <TabsContent value="documents">
                <div className="flex flex-col items-center py-8 text-center text-muted-foreground">
                  <FolderKanban className="mb-2 h-12 w-12 opacity-50" />
                  <p>No documents attached</p>
                  <p className="text-sm">Upload documents from the Documents page</p>
                </div>
              </TabsContent>

              <TabsContent value="finances">
                <div className="flex flex-col items-center py-8 text-center text-muted-foreground">
                  <DollarSign className="mb-2 h-12 w-12 opacity-50" />
                  <p>Financial details</p>
                  {selectedProject.budgetCents != null && (
                    <p className="text-lg font-bold mt-2">
                      Budget: {formatCurrency(selectedProject.budgetCents)}
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </Dialog>
    </div>
  );
}
